<template>
  <div
    class="application-component novelty-analysis-component"
    v-loading="loading"
    :element-loading-text="loadingText"
  >
    <div class="application-component-title">新颖性分析</div>
    <div
      class="application-component-content"
      :style="{ fontSize }"
    >
      <div v-if="readOnly" class="placeholder">
        <el-skeleton :rows="6" animated />
      </div>
      <template v-else>
        <div class="analysis-banner">
          <div class="analysis-banner-title">当前结果基于 Incopat 语义检索初筛</div>
          <div class="analysis-banner-desc">
            新颖性对比表会严格按照所选技术特征生成，系统会基于这些特征自动完成语义检索和对比分析。
          </div>
        </div>

        <div class="search-panel">
          <div class="panel-title-row">
            <div>
              <div class="panel-title">检索特征</div>
              <div class="panel-subtitle">先确认参与对比的技术特征，再执行检索或进入专业检索。</div>
            </div>
            <div v-if="resultData?.updatedAt" class="panel-meta">
              最近更新：{{ resultData.updatedAt }}
            </div>
          </div>

          <template v-if="featureOptions.length">
            <div class="feature-toolbar">
              <div class="feature-count">已选 {{ selectedFeatureCount }}/{{ featureOptions.length }} 项特征</div>
              <div class="search-actions">
                <el-button size="small" @click="handleSelectAllFeatures">全选</el-button>
                <el-button size="small" @click="handleClearFeatures">清空</el-button>
                <el-button type="primary" size="small" :loading="analyzing" @click="handleAutoAnalyze">
                  一键检索
                </el-button>
                <el-button size="small" :loading="openingProfessionalSearch" @click="handleProfessionalSearch">
                  专业检索
                </el-button>
              </div>
            </div>

            <el-checkbox-group v-model="selectedFeatureCodes" class="feature-selector">
              <el-checkbox
                v-for="feature in featureOptions"
                :key="feature.code"
                :value="feature.code"
                class="feature-option"
              >
                <div class="feature-option-inner">
                  <span class="feature-option-code">{{ feature.code }}</span>
                  <span class="feature-option-content">{{ feature.content }}</span>
                </div>
              </el-checkbox>
            </el-checkbox-group>
          </template>

          <el-empty
            v-else
            :image-size="72"
            description="请先生成特征拆解结果"
          />
        </div>

        <div v-if="staleByFeatureChange" class="stale-tip">
          特征拆解内容已发生变化，当前新颖性分析结果可能已过期，请重新选择特征并检索。
        </div>

        <div v-if="rows.length" class="analysis-table-scroll">
          <table class="analysis-table">
            <thead>
              <tr>
                <th class="code-column">编号</th>
                <th class="feature-column">本交底技术特征</th>
                <th
                  v-for="document in documents"
                  :key="document.refCode"
                  class="doc-column"
                >
                  <button class="doc-head" type="button" @click="handlePreviewDocument(document)">
                    <span>{{ document.refCode }}</span>
                    <span>{{ document.pn }}</span>
                    <span>相似度 {{ formatScore(document.semanticsScore) }}</span>
                  </button>
                </th>
                <th class="conclusion-column">对比结论</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rows" :key="row.code">
                <td class="code-column cell-code">{{ row.code }}</td>
                <td class="feature-column">
                  <button class="feature-cell-button" type="button" @click="handleFocusFeature(row)">
                    <div class="feature-cell-content textselection">{{ row.content }}</div>
                  </button>
                </td>
                <td
                  v-for="cell in row.compareItems"
                  :key="`${row.code}-${cell.refCode}`"
                  class="doc-column"
                >
                  <div class="compare-cell">
                    <span :class="['status-tag', getStatusClass(cell.status)]">
                      <el-select
                        v-model="cell.status"
                        size="small"
                        class="status-select"
                        @click.stop
                        @change="handleCompareStatusChange(cell)"
                      >
                        <el-option
                          v-for="option in noveltyStatusOptions"
                          :key="option.value"
                          :label="option.label"
                          :value="option.value"
                        />
                      </el-select>
                    </span>
                    <button
                      v-if="isCompareCellFocusable(cell)"
                      class="compare-text compare-text-button textselection"
                      type="button"
                      @click="handleFocusCompareCell(cell)"
                    >
                      {{ cell.content }}
                    </button>
                    <div v-else class="compare-text textselection">{{ cell.content }}</div>
                  </div>
                </td>
                <td class="conclusion-column">
                  <div class="conclusion-cell">
                    <span :class="['conclusion-tag', getConclusionClass(row.conclusion)]">
                      {{ row.conclusionLabel }}
                    </span>
                    <button
                      v-if="showAddToDetail(row)"
                      class="add-detail-button"
                      type="button"
                      @click="handleAddToDetail(row)"
                    >
                      加入交流清单
                    </button>
                    <div class="analysis-text textselection">{{ row.analysis }}</div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <el-empty
          v-else
          :description="emptyDescription"
        />
      </template>
    </div>

    <div v-if="taskPolling" class="analysis-task-overlay">
      <div class="analysis-task-card">
        <div class="analysis-task-title">新颖性分析进行中</div>
        <div class="analysis-task-stage">{{ overlayStageText }}</div>
        <el-progress
          :percentage="overlayProgressPercent"
          :stroke-width="10"
          :show-text="true"
        />
        <div class="analysis-task-tip">检索分析大约需要3-5分钟，可稍后回来查看。</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ElMessage } from 'element-plus'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useGlobalStore } from '@/store/modules/global'
import { useWritingStore } from '@/store/modules/writing'
import {
  fetchAutoNoveltyAnalysis,
  fetchAutoNoveltyAnalysisTaskStatus,
  fetchManualNoveltyAnalysis,
  fetchSaveComponent,
  fetchSearchNoveltyDocument
} from '@/api/writing'
import { jsonParse } from '@/utils'
import {
  buildNoveltySourceVersion,
  buildNoveltyAttachment,
  buildNoveltyDocumentViewTarget,
  clearNoveltyTaskState,
  getNoveltyTaskStorage,
  isNoveltySourceStale,
  NOVELTY_FILE_ID_PREFIX,
  normalizeNoveltyTaskStatus,
  readNoveltyTaskState,
  writeNoveltyTaskState,
} from './noveltyAnalysisState.mjs'

const FEATURE_BREAKDOWN_EVENT = 'feature-breakdown-focus'
const NOVELTY_COMPONENT_CODE = 'comp_nov'
const DETAIL_LIST_COMPONENT_CODE = 'comp_comm'
const SEARCH_EVENT_TYPE = 'novelty-analysis-selection'
const SEARCH_STORAGE_PREFIX = 'novelty-analysis-search:'
const TASK_POLL_INTERVAL = 5000

const NOVELTY_STATUS_OPTIONS = [
  { value: 'UNDISCLOSED', label: '未公开' },
  { value: 'PARTIAL', label: '部分公开' },
  { value: 'DISCLOSED', label: '已公开' },
]

const globalStore = useGlobalStore()
const writingStore = useWritingStore()
const noveltyTaskStorage = getNoveltyTaskStorage()

const props = defineProps({
  readOnly: {
    type: Boolean,
    default: false
  },
  data: {
    type: Object,
    default: () => null
  },
  projectId: {
    type: String,
    default: ''
  },
  handleFocusMainFileSource: {
    type: Function,
    default: null
  },
  handleFocusCompareFileSource: {
    type: Function,
    default: null
  },
  handleAddToDetailList: {
    type: Function,
    default: null
  },
  handleUpdateDetailList: {
    type: Function,
    default: null
  }
})

const resultData = ref(null)
const selectedFeatureCodes = ref([])
const analyzing = ref(false)
const taskPolling = ref(false)
const previewingDocumentKey = ref('')
const openingProfessionalSearch = ref(false)
const syncedFeatureSnapshot = ref('')

let taskPollTimer = null
let taskPollFailureCount = 0

const fontSize = computed(() => `${globalStore.fontSize}px`)
const loading = computed(() => analyzing.value && !taskPolling.value)
const loadingText = computed(() => {
  if (loading.value) {
    return '正在思考中...'
  }
  return ''
})
const taskStatus = ref({
  taskId: '',
  statusCode: '',
  stageCode: '',
  stageText: '',
  showStage: true,
  progressPercent: 0,
  lastVisibleStageText: '',
  lastVisibleProgressPercent: 0,
  errorMessage: '',
})
const overlayStageText = computed(() => {
  if (!taskStatus.value.showStage && taskStatus.value.lastVisibleStageText) {
    return taskStatus.value.lastVisibleStageText
  }
  return taskStatus.value.stageText || taskStatus.value.lastVisibleStageText || '处理中'
})
const overlayProgressPercent = computed(() => {
  if (!taskStatus.value.showStage && taskStatus.value.lastVisibleProgressPercent) {
    return taskStatus.value.lastVisibleProgressPercent
  }
  return taskStatus.value.progressPercent || taskStatus.value.lastVisibleProgressPercent || 0
})
const documents = computed(() => resultData.value?.documents || [])
const rows = computed(() => resultData.value?.rows || [])
const documentMap = computed(() => {
  return documents.value.reduce((result, item) => {
    result[item.refCode] = item
    return result
  }, {})
})
const featureSnapshot = computed(() => writingStore.step2Info?.step2Component?.comp_fea?.docResult || '')
const featureOptions = computed(() => {
  const parsed = jsonParse(featureSnapshot.value, [])
  if (!Array.isArray(parsed)) {
    return []
  }

  return parsed
    .map((item) => ({
      code: String(item?.code || '').trim(),
      content: String(item?.content || '').trim(),
    }))
    .filter(item => item.code && item.content)
})
const featureCodeSet = computed(() => new Set(featureOptions.value.map(item => item.code)))
const selectedFeatureCount = computed(() => selectedFeatureCodes.value.length)
const currentSourceVersion = computed(() => buildNoveltySourceVersion(featureSnapshot.value))
const staleByFeatureChange = computed(() => {
  return isNoveltySourceStale({
    hasFeatureContent: Boolean(featureSnapshot.value),
    hasResultRows: Boolean(rows.value.length),
    resultSourceVersion: resultData.value?.sourceVersion || '',
    currentSourceVersion: currentSourceVersion.value,
  })
})
const emptyDescription = computed(() => {
  if (!featureOptions.value.length) {
    return '请先生成特征拆解结果'
  }
  if (!selectedFeatureCodes.value.length) {
    return '请先选择需要参与对比的技术特征'
  }
  return '执行检索后将在此生成技术特征对照表'
})

const normalizeResult = (value) => {
  const parsed = typeof value === 'string' ? jsonParse(value, null) : value
  if (!parsed || typeof parsed !== 'object') {
    return null
  }

  return {
    ...parsed,
    sourceVersion: parsed.sourceVersion || '',
    documents: Array.isArray(parsed.documents) ? parsed.documents : [],
    rows: Array.isArray(parsed.rows) ? parsed.rows : []
  }
}

const normalizeSelectedFeatureCodes = (codes = []) => {
  if (!Array.isArray(codes) || !featureCodeSet.value.size) {
    return []
  }

  const result = []
  const usedCodes = new Set()
  codes.forEach((code) => {
    const normalizedCode = String(code || '').trim()
    if (!normalizedCode || usedCodes.has(normalizedCode) || !featureCodeSet.value.has(normalizedCode)) {
      return
    }
    usedCodes.add(normalizedCode)
    result.push(normalizedCode)
  })
  return result
}

const syncSelectedFeatureCodes = (codes = [], fallbackToAll = true) => {
  const normalizedCodes = normalizeSelectedFeatureCodes(codes)
  if (normalizedCodes.length || !fallbackToAll) {
    selectedFeatureCodes.value = normalizedCodes
    return normalizedCodes
  }

  selectedFeatureCodes.value = featureOptions.value.map(item => item.code)
  return selectedFeatureCodes.value
}

const formatScore = (score) => {
  if (score === null || score === undefined || Number.isNaN(Number(score))) {
    return '-'
  }
  return Number(score).toFixed(3)
}

const getStatusClass = (status) => {
  if (status === 'DISCLOSED') {
    return 'is-disclosed'
  }
  if (status === 'PARTIAL') {
    return 'is-partial'
  }
  return 'is-undisclosed'
}

const getStatusLabel = (status) => {
  return noveltyStatusOptions.find(item => item.value === status)?.label || '未公开'
}

const noveltyStatusOptions = NOVELTY_STATUS_OPTIONS

const getConclusionClass = (conclusion) => {
  if (conclusion === 'MODIFY') {
    return 'is-modify'
  }
  if (conclusion === 'DIFFERENCE') {
    return 'is-difference'
  }
  return 'is-novel'
}

const isCompareCellFocusable = (cell) => {
  return Boolean(
    cell &&
    cell.status !== 'UNDISCLOSED' &&
    Array.isArray(cell.sourceRanges) &&
    cell.sourceRanges.length
  )
}

const buildNoveltyAttachments = (documentList = []) => {
  return documentList.map(document => buildNoveltyAttachment(document))
}

const syncStepState = (result) => {
  const currentStep2Info = writingStore.step2Info || {}
  const currentReferenceFiles = Array.isArray(currentStep2Info.step2AttachmentReference)
    ? currentStep2Info.step2AttachmentReference
    : []
  const preservedReferenceFiles = currentReferenceFiles.filter(item => {
    return !String(item?.id || '').startsWith(NOVELTY_FILE_ID_PREFIX)
  })

  writingStore.setStep2Info({
    ...currentStep2Info,
    step2AttachmentReference: [
      ...preservedReferenceFiles,
      ...buildNoveltyAttachments(result?.documents || [])
    ],
    step2Component: {
      ...(currentStep2Info.step2Component || {}),
      [NOVELTY_COMPONENT_CODE]: {
        ...(currentStep2Info.step2Component?.[NOVELTY_COMPONENT_CODE] || {}),
        ...(props.data || {}),
        docResult: JSON.stringify(result)
      }
    }
  })
}

const applyResult = (result, successText) => {
  const normalized = normalizeResult(result)
  if (!normalized) {
    throw new Error('invalid_novelty_result')
  }

  normalized.sourceVersion = normalized.sourceVersion || currentSourceVersion.value
  resultData.value = normalized
  syncedFeatureSnapshot.value = featureSnapshot.value
  syncSelectedFeatureCodes(normalized.rows.map(item => item.code))
  syncStepState(normalized)

  if (successText) {
    ElMessage.success(successText)
  }
}

const persistResult = async () => {
  if (!resultData.value || !props.projectId) {
    return
  }

  const normalized = normalizeResult(resultData.value)
  if (!normalized) {
    throw new Error('invalid_novelty_result')
  }

  normalized.sourceVersion = normalized.sourceVersion || currentSourceVersion.value
  await fetchSaveComponent({
    disclosureProjectId: props.projectId,
    componentTypeCode: NOVELTY_COMPONENT_CODE,
    docResult: JSON.stringify(normalized)
  })
  syncStepState(normalized)
}

const handleCompareStatusChange = async (cell) => {
  if (!cell) {
    return
  }

  cell.statusLabel = getStatusLabel(cell.status)
  try {
    await persistResult()
  } catch (error) {
    console.error('save novelty status failed', error)
    ElMessage.error('新颖性分析保存失败')
  }
}

const getActiveSelectedFeatureCodes = () => {
  const normalizedCodes = syncSelectedFeatureCodes(selectedFeatureCodes.value, false)
  if (normalizedCodes.length) {
    return normalizedCodes
  }
  return []
}

const handleSelectAllFeatures = () => {
  selectedFeatureCodes.value = featureOptions.value.map(item => item.code)
}

const handleClearFeatures = () => {
  selectedFeatureCodes.value = []
}

const clearTaskPollTimer = () => {
  if (taskPollTimer) {
    clearTimeout(taskPollTimer)
    taskPollTimer = null
  }
}

const resetTaskPollingState = () => {
  clearTaskPollTimer()
  taskPollFailureCount = 0
  taskPolling.value = false
  taskStatus.value = {
    taskId: '',
    statusCode: '',
    stageCode: '',
    stageText: '',
    showStage: true,
    progressPercent: 0,
    lastVisibleStageText: '',
    lastVisibleProgressPercent: 0,
    errorMessage: '',
    selectedFeatureCodes: [],
  }
  clearNoveltyTaskState(noveltyTaskStorage, props.projectId)
}

const updateTaskStatus = (payload = {}) => {
  const currentState = normalizeNoveltyTaskStatus(taskStatus.value)
  const incomingState = normalizeNoveltyTaskStatus(payload)
  const nextState = {
    ...currentState,
    taskId: incomingState.taskId || currentState.taskId || '',
    statusCode: incomingState.statusCode || currentState.statusCode || '',
    stageCode: incomingState.stageCode || currentState.stageCode || '',
    stageText: incomingState.stageText || currentState.stageText || '',
    showStage: incomingState.showStage,
    progressPercent: Number.isFinite(Number(payload?.progressPercent))
      ? Number(payload.progressPercent)
      : currentState.progressPercent,
    errorMessage: incomingState.errorMessage,
    selectedFeatureCodes: incomingState.selectedFeatureCodes.length
      ? incomingState.selectedFeatureCodes
      : currentState.selectedFeatureCodes,
  }

  if (nextState.showStage && nextState.stageText) {
    nextState.lastVisibleStageText = nextState.stageText
    nextState.lastVisibleProgressPercent = nextState.progressPercent
  }

  taskStatus.value = nextState
  writeNoveltyTaskState(noveltyTaskStorage, props.projectId, nextState)
}

const scheduleTaskPoll = (taskId, delay = TASK_POLL_INTERVAL) => {
  clearTaskPollTimer()
  taskPollTimer = setTimeout(() => {
    pollTaskStatus(taskId)
  }, delay)
}

const startTaskPolling = (payload = {}) => {
  const taskId = String(payload?.taskId || '').trim()
  if (!taskId) {
    throw new Error('invalid_novelty_task')
  }

  resetTaskPollingState()
  taskPolling.value = true
  updateTaskStatus(payload)
  if (taskStatus.value.selectedFeatureCodes.length) {
    syncSelectedFeatureCodes(taskStatus.value.selectedFeatureCodes, false)
  }
  scheduleTaskPoll(taskId, TASK_POLL_INTERVAL)
}

const pollTaskStatus = async (taskId) => {
  if (!props.projectId || !taskPolling.value || taskId !== taskStatus.value.taskId) {
    return
  }

  const result = await fetchAutoNoveltyAnalysisTaskStatus(props.projectId, taskId)
  if (!result || typeof result !== 'object') {
    taskPollFailureCount += 1
    if (taskPollFailureCount >= 3) {
      analyzing.value = false
      resetTaskPollingState()
      ElMessage.error('新颖性分析任务状态查询失败')
      return
    }
    scheduleTaskPoll(taskId, TASK_POLL_INTERVAL)
    return
  }

  taskPollFailureCount = 0
  updateTaskStatus(result)

  if (result.statusCode === 'COMPLETED') {
    const completedResult = result.result || null
    analyzing.value = false
    resetTaskPollingState()
    if (completedResult) {
      applyResult(completedResult, '新颖性分析已更新')
    } else {
      ElMessage.warning('新颖性分析任务已完成，结果同步中')
    }
    return
  }

  if (result.statusCode === 'FAILED') {
    const errorMessage = String(result.errorMessage || '').trim() || '一键检索失败'
    analyzing.value = false
    resetTaskPollingState()
    ElMessage.error(errorMessage)
    return
  }

  scheduleTaskPoll(taskId)
}

const handleAutoAnalyze = async () => {
  if (!props.projectId || analyzing.value) {
    return
  }

  const nextSelectedFeatureCodes = getActiveSelectedFeatureCodes()
  if (!nextSelectedFeatureCodes.length) {
    ElMessage.warning('请先选择需要参与对比的技术特征')
    return
  }

  analyzing.value = true
  try {
    const effectiveFeatureCodes = [...nextSelectedFeatureCodes]
    const task = await fetchAutoNoveltyAnalysis(props.projectId, {
      selectedFeatureCodes: nextSelectedFeatureCodes,
      topN: 3
    })
    startTaskPolling({
      ...task,
      selectedFeatureCodes: effectiveFeatureCodes,
    })
  } catch (error) {
    console.error('auto novelty analysis failed', error)
    resetTaskPollingState()
    analyzing.value = false
    ElMessage.error('一键检索失败')
  }
}

const handleManualAnalyze = async (selectedItems, nextSelectedFeatureCodes = selectedFeatureCodes.value) => {
  if (!props.projectId || analyzing.value) {
    return
  }
  if (!Array.isArray(selectedItems) || !selectedItems.length) {
    ElMessage.warning('请选择对比文献')
    return
  }

  const normalizedFeatureCodes = syncSelectedFeatureCodes(nextSelectedFeatureCodes, false)
  if (!normalizedFeatureCodes.length) {
    ElMessage.warning('请先选择需要参与对比的技术特征')
    return
  }

  analyzing.value = true
  try {
    const result = await fetchManualNoveltyAnalysis(props.projectId, {
      selectedItems,
      selectedFeatureCodes: normalizedFeatureCodes
    })
    applyResult(result, '专业检索结果已返回')
  } catch (error) {
    console.error('manual novelty analysis failed', error)
    ElMessage.error('专业检索分析失败')
  } finally {
    analyzing.value = false
  }
}

const handleProfessionalSearch = async () => {
  if (!props.projectId || openingProfessionalSearch.value || analyzing.value) {
    return
  }

  const nextSelectedFeatureCodes = getActiveSelectedFeatureCodes()
  if (!nextSelectedFeatureCodes.length) {
    ElMessage.warning('请先选择需要参与对比的技术特征')
    return
  }

  openingProfessionalSearch.value = true
  try {
    const storageKey = `${SEARCH_STORAGE_PREFIX}${props.projectId}:${Date.now()}`
    sessionStorage.setItem(storageKey, JSON.stringify({
      featureOptions: featureOptions.value,
      selectedFeatureCodes: nextSelectedFeatureCodes
    }))
    window.open(`/writing/novelty-search?id=${props.projectId}&key=${encodeURIComponent(storageKey)}`, '_blank')
  } finally {
    openingProfessionalSearch.value = false
  }
}

const handleProfessionalSearchMessage = (event) => {
  if (event.origin !== window.location.origin) {
    return
  }

  const payload = event.data || {}
  if (payload.type !== SEARCH_EVENT_TYPE || payload.projectId !== props.projectId) {
    return
  }

  handleManualAnalyze(payload.selectedItems || [], payload.selectedFeatureCodes || selectedFeatureCodes.value)
}

const openDocumentTarget = (target) => {
  if (!target) {
    return false
  }
  window.open(target, '_blank')
  return true
}

const mergeDocumentPreview = (sourceDocument = {}, payload = {}) => {
  const refCode = String(sourceDocument?.refCode || payload?.refCode || '').trim()
  const pn = String(sourceDocument?.pn || payload?.pn || '').trim()
  const matchedDocument = documents.value.find(item => (
    String(item?.refCode || '').trim() === refCode ||
    String(item?.pn || '').trim() === pn
  ))
  if (!matchedDocument) {
    return {
      ...sourceDocument,
      ...payload
    }
  }
  Object.assign(matchedDocument, payload)
  return matchedDocument
}

const handlePreviewDocument = (document) => {
  loadAndPreviewDocument(document, {
    fallbackTarget: buildNoveltyDocumentViewTarget(props.projectId, document)
  })
}

const loadAndPreviewDocument = async (document = {}, { fallbackTarget = '' } = {}) => {
  const documentKey = `${document?.refCode || ''}:${document?.pn || ''}`
  if (!props.projectId || previewingDocumentKey.value === documentKey) {
    return
  }

  previewingDocumentKey.value = documentKey
  try {
    const result = await fetchSearchNoveltyDocument(props.projectId, {
      refCode: document.refCode,
      pn: document.pn,
      semanticsScore: document.semanticsScore
    })
    const mergedDocument = mergeDocumentPreview(document, result)
    const target = buildNoveltyDocumentViewTarget(props.projectId, mergedDocument)
    if (!openDocumentTarget(target)) {
      if (openDocumentTarget(fallbackTarget)) {
        ElMessage.warning('实时获取失败，已尝试打开已有原文链接')
        return
      }
      ElMessage.warning('暂无可预览文件')
      return
    }
  } catch (error) {
    console.error('load novelty document preview failed', error)
    if (openDocumentTarget(fallbackTarget)) {
      ElMessage.warning('实时获取失败，已尝试打开已有原文链接')
      return
    }
    ElMessage.error('专利原文加载失败，请稍后重试')
  } finally {
    previewingDocumentKey.value = ''
  }
}

const handleFocusFeature = (row) => {
  if (props.handleFocusMainFileSource) {
    props.handleFocusMainFileSource({
      itemId: row?.featureId || row?.code || '',
      sourceRanges: row?.sourceRanges || []
    })
    return
  }
  window.dispatchEvent(new CustomEvent(FEATURE_BREAKDOWN_EVENT, {
    detail: {
      projectId: props.projectId,
      sourceRanges: row?.sourceRanges || []
    }
  }))
}

const handleFocusCompareCell = (cell) => {
  if (!isCompareCellFocusable(cell)) {
    return
  }

  const document = documentMap.value[cell.refCode]
  if (!document) {
    return
  }

  if (props.handleFocusCompareFileSource) {
    props.handleFocusCompareFileSource({
      document,
      cell,
    })
  }
}

const showAddToDetail = (row) => {
  return row?.conclusion !== 'NOVEL' && (props.handleUpdateDetailList || props.handleAddToDetailList)
}

const handleAddToDetail = (row) => {
  const currentList = jsonParse(
    writingStore.step2Info?.step2Component?.[DETAIL_LIST_COMPONENT_CODE]?.docResult || '[]',
    []
  )
  const sourceId = `novelty:${row.code}:${row.conclusion}`

  if (currentList.some(item => item?.sourceId === sourceId)) {
    ElMessage.warning('该问题已加入交流清单')
    return
  }

  const relatedDocuments = (row.compareItems || [])
    .map(item => `${item.refCode}(${item.pn}, ${item.statusLabel})`)
    .join('；')

  const nextItem = {
    type: 'text',
    sourceId,
    question: `${row.code} ${row.conclusionLabel}`,
    answer: `${row.content}\n\n${row.analysis}${relatedDocuments ? `\n\n对比文献：${relatedDocuments}` : ''}`
  }

  if (props.handleUpdateDetailList) {
    props.handleUpdateDetailList([
      ...currentList,
      nextItem
    ])
  } else if (props.handleAddToDetailList) {
    props.handleAddToDetailList(nextItem)
  }

  ElMessage.success('已加入交流清单')
}

watch(
  () => props.data?.docResult,
  (newValue) => {
    const normalized = normalizeResult(newValue)
    resultData.value = normalized
    if (normalized) {
      normalized.sourceVersion = normalized.sourceVersion || currentSourceVersion.value
      syncedFeatureSnapshot.value = featureSnapshot.value
      syncSelectedFeatureCodes(normalized.rows.map(item => item.code))
      return
    }
    syncSelectedFeatureCodes([], true)
  },
  { immediate: true }
)

watch(
  featureOptions,
  () => {
    if (!featureOptions.value.length) {
      selectedFeatureCodes.value = []
      return
    }

    const currentSelection = normalizeSelectedFeatureCodes(selectedFeatureCodes.value)
    if (currentSelection.length) {
      if (currentSelection.length !== selectedFeatureCodes.value.length) {
        selectedFeatureCodes.value = currentSelection
      }
      return
    }

    const rowSelection = normalizeSelectedFeatureCodes(rows.value.map(item => item.code))
    if (rowSelection.length) {
      selectedFeatureCodes.value = rowSelection
      return
    }

    selectedFeatureCodes.value = featureOptions.value.map(item => item.code)
  },
  { immediate: true }
)

onMounted(() => {
  const persistedTaskState = readNoveltyTaskState(noveltyTaskStorage, props.projectId)
  if (persistedTaskState) {
    taskPolling.value = true
    taskStatus.value = persistedTaskState
    analyzing.value = true
    if (persistedTaskState.selectedFeatureCodes.length) {
      syncSelectedFeatureCodes(persistedTaskState.selectedFeatureCodes, false)
    }
    scheduleTaskPoll(persistedTaskState.taskId, 0)
  }
  window.addEventListener('message', handleProfessionalSearchMessage)
})

onUnmounted(() => {
  clearTaskPollTimer()
  taskPollFailureCount = 0
  window.removeEventListener('message', handleProfessionalSearchMessage)
})
</script>

<style scoped lang="scss">
@import './common.scss';

.novelty-analysis-component {
  position: relative;
  min-height: 0;
}

.analysis-task-overlay {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(2px);
}

.analysis-task-card {
  width: min(420px, 100%);
  padding: 20px 22px;
  border: 1px solid #d9f99d;
  border-radius: 18px;
  background: linear-gradient(180deg, #f7fee7 0%, #ffffff 100%);
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.12);
}

.analysis-task-title {
  color: #14532d;
  font-size: 16px;
  font-weight: 700;
}

.analysis-task-stage {
  margin: 10px 0 14px;
  color: #334155;
  line-height: 1.7;
}

.analysis-task-tip {
  margin-top: 12px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.6;
}

.analysis-banner,
.feature-cell-source {
  display: none;
}

.analysis-banner,
.search-panel,
.stale-tip {
  padding: 14px 16px;
  border-radius: 14px;
}

.analysis-banner {
  background: linear-gradient(180deg, #f5f9f7 0%, #edf7f3 100%);
  border: 1px solid #cfe8dc;
}

.analysis-banner-title {
  color: #14532d;
  font-size: 14px;
  font-weight: 600;
}

.analysis-banner-desc {
  margin-top: 6px;
  color: #4b5563;
  line-height: 1.7;
}

.search-panel {
  margin-top: 14px;
  border: 1px solid #e5e7eb;
  background: #fff;
}

.panel-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.panel-title {
  color: #111827;
  font-weight: 600;
}

.panel-subtitle,
.panel-meta {
  color: #6b7280;
  font-size: 12px;
  line-height: 1.6;
}

.panel-subtitle {
  margin-top: 4px;
}

.feature-toolbar,
.search-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}

.feature-count {
  color: #0f172a;
  font-size: 13px;
  font-weight: 600;
}

.feature-selector {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
  max-height: 320px;
  overflow: auto;
  padding-right: 4px;
}

.feature-option {
  width: 100%;
  height: auto;
  margin-right: 0;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #f8fafc;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:hover {
    border-color: #99f6e4;
    background: #f0fdfa;
  }

  :deep(.el-checkbox__input) {
    margin-top: 2px;
  }

  :deep(.el-checkbox__label) {
    flex: 1;
    min-width: 0;
    padding-left: 10px;
    white-space: normal;
  }

  :deep(.el-checkbox__inner) {
    border-color: #94a3b8;
  }
}

.feature-option-inner {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  line-height: 1.6;
}

.feature-option-code {
  color: #0f766e;
  font-size: 12px;
  font-weight: 700;
}

.feature-option-content {
  color: #111827;
  white-space: normal;
  word-break: break-word;
}

.feature-selector :deep(.el-checkbox) {
  width: 100%;
  height: auto;
  margin-right: 0;
  align-items: flex-start;
}

.feature-selector :deep(.el-checkbox__label) {
  display: block;
}

.feature-selector :deep(.el-checkbox.is-checked) {
  border-color: #14b8a6;
  background: #ecfdf5;
}

.stale-tip {
  margin-top: 12px;
  color: #9a3412;
  background: #fff7ed;
  border: 1px solid #fdba74;
}

.analysis-table-scroll {
  margin-top: 16px;
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
}

.analysis-table {
  min-width: 920px;
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;

  th,
  td {
    padding: 14px 12px;
    vertical-align: top;
    border-bottom: 1px solid #e5e7eb;
    border-right: 1px solid #e5e7eb;
  }

  th:last-child,
  td:last-child {
    border-right: none;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f8fafc;
    color: #111827;
    font-weight: 600;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
}

.code-column {
  width: 78px;
}

.feature-column {
  width: 240px;
}

.doc-column {
  width: 220px;
}

.conclusion-column {
  width: 260px;
}

.cell-code {
  color: #111827;
  font-weight: 700;
}

.doc-head,
.feature-cell-button {
  width: 100%;
  padding: 0;
  font: inherit;
  color: inherit;
  text-align: left;
  background: transparent;
  border: none;
}

.doc-head {
  display: flex;
  flex-direction: column;
  gap: 4px;
  cursor: pointer;

  span:first-child {
    color: #0f766e;
    font-weight: 700;
  }

  span:last-child {
    color: #6b7280;
    font-size: 12px;
  }
}

.feature-cell-button {
  cursor: pointer;
}

.feature-cell-content {
  color: #111827;
  line-height: 1.7;
}

.compare-cell,
.conclusion-cell {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.status-tag,
.conclusion-tag {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
}

.status-tag.is-undisclosed {
  color: #4b5563;
  background: #f3f4f6;
}

.status-tag.is-partial {
  color: #b45309;
  background: #ffedd5;
}

.status-tag.is-disclosed {
  color: #b91c1c;
  background: #fee2e2;
}

.status-select {
  width: 92px;

  :deep(.el-select__wrapper) {
    min-height: 24px;
    padding: 0;
    background: transparent;
    border: none;
    box-shadow: none;
  }

  :deep(.el-select__selected-item) {
    color: inherit;
    font-size: 12px;
    font-weight: 600;
  }

  :deep(.el-select__placeholder) {
    color: inherit;
  }
}

.conclusion-tag.is-novel {
  color: #166534;
  background: #dcfce7;
}

.conclusion-tag.is-difference {
  color: #b45309;
  background: #ffedd5;
}

.conclusion-tag.is-modify {
  color: #b91c1c;
  background: #fee2e2;
}

.compare-text,
.analysis-text {
  font-size: inherit;
  color: #4b5563;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
}

.compare-text-button {
  display: block;
  width: 100%;
  padding: 0;
  font: inherit;
  text-align: left;
  background: transparent;
  border: none;
  cursor: pointer;

  &:hover {
    color: #0f766e;
  }
}

.add-detail-button {
  width: fit-content;
  padding: 0;
  color: #0f766e;
  font-size: 13px;
  background: transparent;
  border: none;
  cursor: pointer;
}

.placeholder {
  :deep(.el-skeleton__item) {
    border-radius: 8px;
  }
}

@media (max-width: 960px) {
  .panel-title-row,
  .feature-toolbar {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
